'use client';

/**
 * ComposerLinesCard — cart line items, paint-matched to rail Insights cards.
 *
 * Reads `useCartStore.items` directly. Per-line: product image, name,
 * vendor (when bound), qty stepper, line subtotal, remove. Header
 * (via the shared `<RailCard />` primitive) carries an intent-tag
 * badge surfacing `activeIntent`. Footer shows cart-wide total
 * (intent-aware via `getTotal()` — RFQ flow returns 0 by design and
 * we hide the row).
 *
 * Submission (project / vendor / deadline / brief / send) lives in
 * `<ComposerSubmitCard />`, a sibling composer card — the legacy
 * `<CartDrawer />` retired, this card never depended on it.
 *
 * Mounted by `<ComposerSurface />` via `COMPOSER_CARD_REGISTRY`.
 *
 * @see klyde_edge/composition/cards/composer.py — composer_lines spec
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain procurement
 */

import { useMemo, useState } from 'react';
import { Package, X, Plus, Minus, ShoppingCart, DoorOpen, GitCompare } from 'lucide-react';
import { useCardExport } from '@/lib/design/card-export';
import {
  useCartStore,
  type CartItem,
  getItemPrice,
  getItemVendorName,
} from '@/lib/stores/cart-store';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '@/components/copilot/rail-insights/rail-card';
import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { useAffordance, useComposerLabel } from '@/lib/stores/total-matrix-store';

export function ComposerLinesCard() {  const items = useCartStore((s) => s.items);
  const activeIntent = useCartStore((s) => s.activeIntent);
  const removeItem = useCartStore((s) => s.removeItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const setItemSpec = useCartStore((s) => s.setItemSpec);
  const openLinesOverlay = useAgentCanvasStore((s) => s.openLinesOverlay);
  const openCostPlanReview = useAgentCanvasStore((s) => s.openCostPlanReview);
  const editingMcpSid = useCartStore((s) => s.editingMcpSid);
  const clearItems = useCartStore((s) => s.clearItems);
  const editAllTestId = useAffordance('composer.lines.edit_all');
  const compareVendorsLabel = useComposerLabel('lines_compare_vendors');
  const assignRoomsLabel = useComposerLabel('lines_assign_rooms');
  const clearActionLabel = useComposerLabel('lines_clear_action');
  // Spec line is an MCP-only render concern (lands on the cost plan
  // PDF under the product name). RFQ cart payload doesn't carry it.
  const showSpec = activeIntent === 'mcp';

  // Scene-only export — the cart lines (draft), painted as the user sees.
  useCardExport(useMemo(() => ({
    label: items.length ? `Cart (${items.length})` : 'Cart',
    content_key: 'composer_lines',
    surface: 'composer' as const,
    status: 'loaded' as const,
    query_key: null,
    text: items.length
      ? items.map((i) => `${i.name} × ${i.quantity}`).join('\n')
      : 'Cart empty',
    json: {
      count: items.length,
      items: items.map((i) => {
        const p = getItemPrice(i);
        return {
          line_id: i.line_id,
          name: i.name,
          qty: `× ${i.quantity}`,
          price: p != null ? `$${Number(p).toFixed(0)}` : null,
          vendor: getItemVendorName(i) ?? null,
        };
      }),
    },
  }), [items]));

  // "Assign rooms" opens the concertina's room-allocation board — the
  // one job that stays overlay-scoped (a line's qty split across
  // several room-programme slots needs a master-detail layout a flat
  // row can't express). Used to also open a dense qty/price/spec table
  // ('table' tab) for bulk-editing past the ~10-line point these
  // per-line cards don't scale to; that job now lives directly on the
  // canvas pivot table's Line-mode rows (mcp_line_edit/mcp_line_remove,
  // commits immediately) — see klyde_ipe/docs/mcp-mock-e2e/HANDOVER.md.
  // Always shown once the cart has anything, not gated behind a count
  // threshold — a hidden-until-N-items button can't be discovered by
  // the person it's built for until their cart is already big.
  const clearAction =
    items.length > 0 ? (
      // Icon-only, not icon+label — three text-labeled actions ("Assign
      // rooms" / "Compare across vendors" / "Clear") were eating enough
      // header width to truncate the card's own "Lines" title (RailCard's
      // title is flex-1/truncate by design, actions are shrink-0, so a
      // wide actions block squeezes the title first). Tooltips (title=)
      // carry the full label for discoverability instead.
      <div className="flex items-center gap-2.5">
        <button
          data-testid={editAllTestId}
          onClick={() => openLinesOverlay()}
          className="hover:text-primary transition-colors text-muted-foreground"
          title={assignRoomsLabel}
        >
          <DoorOpen className="w-3.5 h-3.5" />
        </button>
        {/* "Compare across vendors" — peer trigger to "Assign rooms",
            opens the Cost Comparison overlay (ObeyaCostPlanComparisonCard,
            mode-independent per agent-canvas-overlay.tsx) against the
            plan the cart is currently targeting. Gated on editingMcpSid
            being set — a brand-new, not-yet-saved plan has nothing to
            compare against yet (same reasoning RoomBoardBody's
            `needsRequirementPick` applies to editingMcpSid). The card
            itself resolves editingMcpSid as its last fallback (see
            costplan-comparison-card.tsx), so no mcpId needs passing here. */}
        {editingMcpSid && (
          <button
            onClick={() => openCostPlanReview()}
            className="hover:text-primary transition-colors text-muted-foreground"
            title={compareVendorsLabel}
          >
            <GitCompare className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onClick={clearItems}
          className="hover:text-destructive transition-colors text-muted-foreground"
          title={clearActionLabel}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    ) : undefined;

  if (items.length === 0) {
    return (
      <RailCard title="Lines" icon={ShoppingCart}>
        <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
          <ShoppingCart className="w-7 h-7 mb-1.5 opacity-40" />
          <p className={text.body}>Cart is empty</p>
          <p className={cn(text.muted, 'text-muted-foreground')}>Add products from a vendor catalog</p>
        </div>
      </RailCard>
    );
  }

  return (
    <RailCard title="Lines" icon={ShoppingCart} actions={clearAction}>
      {/* Flat divided list, not seven bordered cards — reads as one
          cart, not seven objects. The canvas pivot table already IS
          the "cards" plane; this list doesn't need to compete with it. */}
      <ul className="divide-y divide-border/60 -mx-2">
        {items.map((item) => (
          <ComposerLineRow
            key={item.line_id}
            item={item}
            showSpec={showSpec}
            onUpdateQuantity={updateQuantity}
            onUpdateSpec={setItemSpec}
            onRemove={removeItem}
          />
        ))}
      </ul>
    </RailCard>
  );
}

function ComposerLineRow({
  item,
  showSpec,
  onUpdateQuantity,
  onUpdateSpec,
  onRemove,
}: {
  item: CartItem;
  showSpec: boolean;
  onUpdateQuantity: (line_id: string, quantity: number) => void;
  onUpdateSpec: (line_id: string, spec: string | null) => void;
  onRemove: (line_id: string) => void;
}) {  const price = getItemPrice(item);
  const vendor = getItemVendorName(item);
  const removeTestId = useAffordance('composer.lines.remove', { line_id: item.line_id });
  const decrementTestId = useAffordance('composer.lines.decrement_qty', { line_id: item.line_id });
  const incrementTestId = useAffordance('composer.lines.increment_qty', { line_id: item.line_id });
  // Disclosure, not an always-rendered field — on a real cart most
  // lines have no spec yet, and a blank input per line was ~40px × N
  // of empty chrome. Opens by default only if this line already has
  // one (editing an existing value shouldn't require a click first).
  const [specOpen, setSpecOpen] = useState(!!item.spec);

  return (
    <li className="px-1 py-1.5 hover:bg-muted/30 transition-colors">
      {/* Two lines total (was four: title / vendor / qty+price / spec)
          — ~52px per line instead of ~150px. Thumbnail spans both. */}
      <div className="flex items-center gap-2">
        {item.image_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={item.image_url}
            alt={item.name}
            className="h-8 w-8 rounded object-cover flex-shrink-0"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded bg-muted flex-shrink-0">
            <Package className="h-3.5 w-3.5 text-muted-foreground/60" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          {/* Line 1 — title + vendor combined, remove at the end. */}
          <div className="flex items-center justify-between gap-2">
            <p className={cn(text.body, 'truncate font-medium min-w-0')}>
              {item.name}
              {vendor && <span className={cn(text.muted, 'font-normal text-muted-foreground')}> · {vendor}</span>}
            </p>
            <button
              data-testid={removeTestId}
              onClick={() => onRemove(item.line_id)}
              className="p-1 -m-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0"
              title="Remove from cart"
            >
              <X className="h-3 w-3" />
            </button>
          </div>

          {/* Line 2 — qty stepper left, value right. $0 reads as a
              priced zero; most of these are pre-quote and genuinely
              unpriced, so an unset/zero price renders as — instead
              (same fix as the canvas pivot table's footer/KPIs). */}
          <div className="flex items-center justify-between mt-0.5">
            <div className="flex items-center gap-0.5 bg-muted rounded p-0.5">
              <button
                data-testid={decrementTestId}
                onClick={() => onUpdateQuantity(item.line_id, item.quantity - 1)}
                className="h-5 w-5 flex items-center justify-center rounded bg-background text-foreground hover:bg-accent transition-colors"
              >
                <Minus className="h-2.5 w-2.5" />
              </button>
              <input
                type="number"
                value={item.quantity}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10) || 1;
                  onUpdateQuantity(item.line_id, Math.max(1, val));
                }}
                className={cn(
                  text.body,
                  'w-8 h-5 text-center font-medium bg-transparent border-0 focus:outline-none focus:ring-0',
                )}
                min="1"
              />
              <button
                data-testid={incrementTestId}
                onClick={() => onUpdateQuantity(item.line_id, item.quantity + 1)}
                className="h-5 w-5 flex items-center justify-center rounded bg-background text-foreground hover:bg-accent transition-colors"
              >
                <Plus className="h-2.5 w-2.5" />
              </button>
            </div>

            {price ? (
              <span className={text.value}>
                ${(price * item.quantity).toLocaleString()}
              </span>
            ) : (
              <span className={cn(text.muted, 'text-muted-foreground')}>—</span>
            )}
          </div>

          {/* MCP-only spec — buyer-typed editorial text that prints
              under the product name on the cost plan PDF. Disclosure:
              a "+ spec" link until clicked, not a field on every line
              whether or not it's used. Room assignment used to have
              its own row/button here too; it's gone now that the
              lines overlay's Rooms tab is the one place that edits
              room placement — a per-line rail trigger was a second
              dialogue for the same job once that overlay existed. */}
          {showSpec && (
            specOpen ? (
              <input
                type="text"
                autoFocus={!item.spec}
                value={item.spec ?? ''}
                onChange={(e) => onUpdateSpec(item.line_id, e.target.value)}
                placeholder="Spec…"
                className={cn(
                  text.muted,
                  'mt-0.5 w-full px-1.5 py-0.5 rounded border border-border/60 bg-background text-muted-foreground',
                  'focus:outline-none focus:ring-1 focus:ring-primary',
                )}
              />
            ) : (
              <button
                type="button"
                onClick={() => setSpecOpen(true)}
                className={cn(text.muted, 'mt-0.5 text-muted-foreground hover:text-primary transition-colors')}
              >
                + spec
              </button>
            )
          )}
        </div>
      </div>
    </li>
  );
}
